/**
 * A value picked into both lists at once.
 *
 * The API applies `(i1 OR i2 OR …) AND NOT e1 AND NOT e2`, so an
 * overlapping value is dropped from the result — the exclusion is
 * applied to the already-narrowed set. What that means for the user
 * depends on how much overlaps, and the two cases are genuinely
 * different:
 *
 *   - EVERY included value is also excluded → the two clauses cancel out
 *     and the table will come back empty. Worth stopping to read.
 *   - SOME of them overlap → those values drop out, the rest still show.
 *     That is the documented behaviour working as intended, not a
 *     mistake, so it gets a note rather than a warning.
 *
 * Reporting "this returns nothing" for the second case would be wrong,
 * and a warning that cries wolf is one the user learns to click past.
 *
 * Exclude-only is never a conflict: with no include clause an exclusion
 * means "everything except these", which is the common case.
 */
export const CONFLICT_KINDS = { none: 'none', partial: 'partial', empty: 'empty' };

export const findConflict = ({ include = [], exclude = [] } = {}) => {
  const overlap = include.filter(value => exclude.includes(value));

  if (overlap.length === 0) {
    return { kind: CONFLICT_KINDS.none, values: [] };
  }
  if (overlap.length === include.length) {
    return { kind: CONFLICT_KINDS.empty, values: overlap };
  }
  return { kind: CONFLICT_KINDS.partial, values: overlap };
};
